import { ScreenBase } from "@frui.ts/screens";
import { interfaces } from "inversify";
import LocalizationService from "services/localizationService";
import { action, computed, observable } from "mobx";
import EnumService from "services/enumService";
import { bound } from "@frui.ts/helpers";
import { startOfCzechDay } from "utils/dateUtils";
import ChargeValidityRuleResponse from "entities/chargeValidityRuleResponse";
import ChargeOrderChargeItem from "models/chargeOrderChargeItem";
import ChargeOrderService from "services/chargeOrderService";
import ChargeOrderItem from "models/chargeOrderItem";
import TimeService from "services/timeService";

export default class ValiditySelectViewModel extends ScreenBase {
  private intervalId?: any;
  readonly IDLE_TIMER_TIMEOUT = 100 * 60;
  readonly IDLE_PROMPT_TIMEOUT = 100 * 60 * 4;
  readonly IDLE_TIMER_DEBOUNCE = 250;
  @observable validSinceDaysBefore: number;
  @observable chargeValidityRules: ChargeValidityRuleResponse[];
  @observable postponedValidityRule: ChargeValidityRuleResponse | undefined;
  @observable counter: number;

  @bound
  async handleIdle() {
    this.counter = this.IDLE_PROMPT_TIMEOUT;
    this.intervalId = setInterval(async () => {
      this.counter = this.counter - 1000;
      if (this.counter === 0 && this.chargeOrderChargeItem.validSince) {
        this.chargeValidityRules = await this.enumService.lazyValues("charge_validity_rules");
        this.getPostponedValidityMaxDays(this.serverTime ?? new Date(), this.chargeValidityRules);
      }
      if (this.counter === 0) {
        this.clearTimeoutInterval();
        this.handleIdle();
      }
    }, 1000);
  }

  @bound
  clearTimeoutInterval() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  constructor(
    public chargeOrderChargeItem: ChargeOrderChargeItem | ChargeOrderItem,
    public chargeOrderService: ChargeOrderService,
    public timeService: TimeService,
    public localizationService: LocalizationService,
    public enumService: EnumService
  ) {
    super();
    this.initialize();
  }

  @computed
  get isDifferentTimeZone() {
    return this.timeService.isFetched && !this.timeService.isServerTimeSame;
  }

  @computed
  get serverTime() {
    return this.timeService.currentServerTime;
  }

  async initialize() {
    await this.timeService.getCurrentServerTime();
    this.chargeValidityRules = await this.enumService.lazyValues("charge_validity_rules");
    this.validSinceDaysBefore = this.chargeOrderService.earliestPossibleValidSinceDaysBefore;
    this.getPostponedValidityMaxDays(this.serverTime ?? new Date(), this.chargeValidityRules);
  }

  async onActivate() {
    await super.onActivate();
  }
  deactivate(close: boolean): Promise<void> {
    this.clearTimeoutInterval();
    return super.deactivate(close);
  }

  @action.bound
  getPostponedValidityMaxDays(validSince: Date | undefined, chargeValidityRules: ChargeValidityRuleResponse[]) {
    if (validSince && chargeValidityRules) {
      const filteredChargeValidityRules = chargeValidityRules.find(
        rule =>
          validSince &&
          rule.validSinceDate &&
          startOfCzechDay(new Date(rule.validSinceDate)) <= validSince &&
          (!rule.validUntilDate || startOfCzechDay(new Date(rule.validUntilDate)) >= validSince)
      );
      this.postponedValidityRule = filteredChargeValidityRules ?? undefined;
    }
  }

  static Factory({ container }: interfaces.Context) {
    return (chargeOrderChargeItem: ChargeOrderChargeItem | ChargeOrderItem) => {
      return new ValiditySelectViewModel(
        chargeOrderChargeItem,
        container.get(ChargeOrderService),
        container.get(TimeService),
        container.get(LocalizationService),
        container.get(EnumService)
      );
    };
  }
}
